import { isScorecardTermElement, ScorecardElementSymbol } from '../../../Scorecard';
import { ElementTileEmoticonIndicatorIconName } from './ElementTileEmoticonIndicator';
import { getScoreHealthRangeTermMaxRangeValue } from '../ScoreHealthRange/util';
import { darkModeColors, displayRate, displayTerm } from '../../../util';

const emojiColors = {
  happyFace: darkModeColors.accent,
  sadFace: darkModeColors.danger,
  neutralFace: darkModeColors.warning,
};

export const useEmojiCalculation = (
  financialElementAbbreviation: ScorecardElementSymbol,
  beachMode: boolean,
  scoreValue?: number | null,
  targetMin?: number | null,
  targetMax?: number | null
) => {
  if (scoreValue == null || targetMin == null || targetMax == null) {
    return { icon: null, displayRange: null };
  }

  const isTermElement = isScorecardTermElement(financialElementAbbreviation);
  const formatTargetRangeValueDisplay: (value?: number) => number | string = (value) =>
    isTermElement ? displayTerm(value) : displayRate(value, { withSymbol: true, zeroIsDash: false });

  const SCALING_FACTOR = 100;
  const TERM_ELEMENT_MAX_MULTIPLIER = getScoreHealthRangeTermMaxRangeValue(1);
  const globalMinScaled = 0;
  const globalMaxScaled = isTermElement ? targetMax * TERM_ELEMENT_MAX_MULTIPLIER * SCALING_FACTOR : SCALING_FACTOR;
  const scoreValueScaled = scoreValue * SCALING_FACTOR;
  const targetMinScaled = targetMin * SCALING_FACTOR;
  const targetMaxScaled = targetMax * SCALING_FACTOR;

  const positiveComparators: Comparator[] = [
    isTermElement ? (target) => [target < globalMinScaled, 'happyFace'] : () => [false, 'happyFace'], // negative values in term scores are always happy
    (target) => [target >= targetMinScaled, 'happyFace'],
    (target) => [target >= targetMinScaled - (targetMinScaled - globalMinScaled) / 2, 'neutralFace'],
    (target) => [target >= globalMinScaled, 'sadFace'],
  ];
  const negativeElements: ScorecardElementSymbol[] = ['Br', 'Tr', 'Dr']; // e.g., more debt is "bad", less debt is "good"
  const negativeComparators: Comparator[] = [
    isTermElement ? (target) => [target < globalMinScaled, 'happyFace'] : () => [false, 'happyFace'], // negative values in term scores are always happy
    (target) => [target > targetMaxScaled + (globalMaxScaled - targetMaxScaled) / 2, 'sadFace'],
    (target) => [target > targetMaxScaled, 'neutralFace'],
    (target) => [target <= targetMaxScaled, 'happyFace'],
  ];

  const icon = findIndex(
    beachMode,
    negativeElements.includes(financialElementAbbreviation) ? negativeComparators : positiveComparators,
    scoreValueScaled
  );

  return {
    icon,
    color: icon ? emojiColors[icon] : null,
    displayRange: icon
      ? targetMin === targetMax
        ? formatTargetRangeValueDisplay(targetMin)
        : `${formatTargetRangeValueDisplay(targetMin)}-${formatTargetRangeValueDisplay(targetMax)}`
      : null,
  };
};

type Comparator = (target: number) => [boolean, ElementTileEmoticonIndicatorIconName];

function findIndex(
  beachMode: boolean,
  comparators: Comparator[],
  target: number
): ElementTileEmoticonIndicatorIconName | null {
  if (beachMode) {
    return 'happyFace';
  }
  for (let i = 0; i < comparators.length; i++) {
    const [matched, iconName] = comparators[i](target);
    if (matched) {
      return iconName;
    }
  }
  return null;
}
